import { LitElement, css, html } from "lit";
import { defineChromeElement, pressFeedback } from "../chrome/ChromeElement";
import { artImage, artMask, type ChromeArt } from "../chrome/chromeArt";
import { chromeControlStyles } from "../chrome/chromeStyles";
import { fitTexts } from "../chrome/fitText";
import type { ActiveAutoSpinButtonBindings, ActiveAutoSpinButtonPort } from "./AutoSpinPorts";

export const ACTIVE_AUTOSPIN_TAG = "slot-autospin-active";

/**
 * `<slot-autospin-active>`: the "Autospins left" stop button that replaces the spin button while
 * autospin runs — the spin back at #d6d6d6, the title, the remaining count and the purple stop icon.
 */
export class ActiveAutoSpinElement extends LitElement implements ActiveAutoSpinButtonPort {
  public static override properties = {
    art: { attribute: false },
    size: { type: Number },
    label: { type: String },
    shown: { type: Boolean, reflect: true },
    enabled: { type: Boolean, reflect: true },
    amount: { state: true },
  };

  public declare art: ChromeArt;
  public declare size: number;
  public declare label: string;
  public declare shown: boolean;
  public declare enabled: boolean;
  private declare amount: string;
  private bindings: ActiveAutoSpinButtonBindings | null = null;

  public constructor() {
    super();
    this.art = {};
    this.size = 300;
    this.label = "Autospins left";
    this.shown = false;
    this.enabled = true;
    this.amount = "";
  }

  public static override styles = [
    chromeControlStyles,
    css`
      :host { display: block; position: relative; color: #fff; font-weight: 500; }
      :host(:not([shown])) { display: none; }
      button { position: absolute; inset: 0; border-radius: 50%; filter: brightness(0.84); }
      button:hover { filter: brightness(0.83); }
      button:active { filter: brightness(1); }
      button[disabled] { filter: brightness(0.49); }
      .back { position: absolute; inset: 0; }
      .row { position: absolute; left: 50%; transform: translate(-50%, -50%); display: flex; align-items: center; justify-content: center; overflow: hidden; }
      .title { text-align: center; white-space: pre-wrap; }
      .stop { position: absolute; left: 50%; transform: translate(-50%, -50%); }
    `,
  ];

  public bind(bindings: ActiveAutoSpinButtonBindings): void {
    this.bindings = bindings;
  }

  public setVisible(visible: boolean): void {
    this.shown = visible;
  }

  public setAmount(text: string): void {
    this.amount = text;
  }

  public setInteractable(interactable: boolean): void {
    this.enabled = interactable;
  }

  protected override updated(): void {
    fitTexts(this.renderRoot);
  }

  protected override render() {
    // Widget-driven boxes inside the 300² prefab scale with the slot.
    const k = this.size / 300;
    return html`
      <button type="button" ?disabled=${!this.enabled} @pointerdown=${pressFeedback} @click=${() => this.bindings?.onStop()}>
        <div class="back art" style=${artImage(this.art, "spinBtnBack")}></div>
        <div class="row title" style="top: ${(150 - 71.94) * k}px; width: ${244.2 * k}px; height: ${134.1 * k}px; font-size: ${25.4 * k}px"><span data-fit style="white-space: pre-wrap">${this.label}</span></div>
        <div class="stop mask" style="top: ${(150 + 19.12) * k}px; width: ${130 * k}px; height: ${130 * k}px; ${artMask(this.art, "slamIcon", "#d941ee")}"></div>
        <div class="row shadow" data-part="amount" style="top: ${(150 + 19.21) * k}px; left: calc(50% - ${1.275 * k}px); width: ${152.1 * k}px; height: ${148.6 * k}px; font-size: ${48.7 * k}px"><span data-fit>${this.amount}</span></div>
      </button>
    `;
  }
}

export function defineActiveAutoSpinElement(): void {
  defineChromeElement(ACTIVE_AUTOSPIN_TAG, ActiveAutoSpinElement);
}
